// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getType, transformToString } from "../../utils/utils";
import { logoutUser } from "./logoutTrigger";
import Cookies from "js-cookie";

const sanitizeInvalidJsonNumbers = (text) =>
  text
    .replace(/\bNaN\b/g, "null")
    .replace(/\bInfinity\b/g, "null")
    .replace(/\b-Infinity\b/g, "null");

const parseJsonAllowingInvalidNumbers = (text) => {
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    const sanitized = sanitizeInvalidJsonNumbers(text);
    if (sanitized === text) {
      throw error;
    }

    return JSON.parse(sanitized);
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers) => {
    const token = Cookies.get('access_token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('ngrok-skip-browser-warning', 'true');
    return headers;
  },
  responseHandler: async (response) => {
    const contentType = response.headers.get("content-type") || "";
    const text = await response.text();
    if (!contentType.includes("application/json")) {
      return text;
    }

    return parseJsonAllowingInvalidNumbers(text);
  },
});

const customBaseQuery = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (
    result?.error?.data?.errors?.error ===
    'Your session has timed out, please sign in again.'
  ) {
     logoutUser(api);
  }

  return result;
};

// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: "api",
  // All of our requests will have URLs starting with '/fakeApi'
  baseQuery: customBaseQuery,
  keepUnusedDataFor: 60 * 60,
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    // The `getPosts` endpoint is a "query" operation that returns data
    getSupplierTable: builder.query({
      // The URL for the request is '/fakeApi/posts'
      query: (args) => {
        const { id } = args;
        return `/get_pricing_report_items?seller_account_id=${args.id}`;
      },
    }),

    // getSupplierTableWithPagination: builder.query({
    //   // The URL for the request is '/fakeApi/posts'
    //   query: (args) => {
    //     const { id } = args;
    //     return `/get_pricing_report_items?seller_account_id=${args.id}&from=${args.from}&size=${args.to}`;
    //   },
    // }),

    getSupplierTableWithSorting: builder.query({
      // The URL for the request is '/fakeApi/posts'
      query: (args) => {
        if (args.from === undefined || args.from === null) args.from = 0;
        if (args.size === undefined || args.size === null) args.size = 15;
        if (args.sort_by === undefined || args.sort_by === null)
          args.sort_by = "sell_offer_cents";
        if (args.asc_desc === undefined || args.asc_desc === null)
          args.asc_desc = "asc";

        return `/get_pricing_report_items?seller_account_id=${args.id}&from=${
          args.search && args.search.length > 0 ? 0 : args.from
        }&size=${args.size}&sort_by=${encodeURIComponent(
          args.sort_by
        )}&asc_desc=${args.asc_desc} ${
          args.search && args.search.length > 0
            ? `&cert_stock_number=${args.search}`
            : " "
        }${
          args.is_filter_locked !== null &&
          args.is_filter_locked !== undefined &&
          args.is_filter_locked !== ""
            ? `&is_filter_locked=${encodeURIComponent(args.is_filter_locked)}`
            : ""
        }`;
        // return `/get_pricing_report_items?seller_account_id=${args.id}&from=${args.from}&size=${args.size}&sort_by=${args.sort_by}&asc_desc=${args.asc_desc}`;
      },
      providesTags: ["priceTable"],
    }),

    getMarketMatchingItems: builder.query({
      query: (args) => {
        if (args.from === undefined || args.from === null) args.from = 0;
        if (args.size === undefined || args.size === null) args.size = 50;
        if (args.sortBy === undefined || args.sortBy === null)
          args.sortBy = "sell_offer_cents";
        return `/pricing_market_items?item_id=${args.itemId}&account_id=${args.accId}&pricing_report_result_id=${args.prrId}&is_reset=${args.isReset}&is_filter=${args.isFilter}&is_default=${args.isDefault}&lock=${args.lock}&from=${args.from}&size=${args.size}&sort_by=${encodeURIComponent(args.sortBy)}`;
      }
    }),

    getColorClarityDistribution: builder.query({
      query: (args) => {
        const { 
          startDate, 
          endDate, 
          shape = '0000round', 
          caratRange,
          filters  
        } = args;

        return {
          url: `/bi_analysis_color_clarity_distribution?` +
               `seller_account_id=${args.id}` +
               `&start_date=${startDate}` +
               `&end_date=${endDate}` +
               `&shape=${shape}` +
               `&carat_range=${caratRange}`,
          method: "POST",
          body: filters, // Send filters in the request body
        };
      },
    }),
    getIndividualMovementHistory: builder.query({
      query: (args) => {
        return `/market_intelligence_movement_history?` +
          `seller_account_id=${args.id}` +
          `&shape=${args.shape}` +
          `&carat_range=${args.carat_range}`+
          `&enumlocation=${args.location}` +
          `&duration_in_weeks=${args.duration_in_weeks}`
      },
    }),

    getTestIndividualMovementHistory: builder.query({
      query: (args) => {
        return `/market_intelligence_movement_history?` +
          `seller_account_id=${args.id}` +
          `&${args.prop_name}=${args.prop_value}` 
          
      },
    }),
    getHigherLevelAnalysisColorClarity: builder.query({
      query: (args) => {
        const { 
          // startDate, 
          // endDate, 
          shape = '0000round', 
          caratRange = '1.00-1.09' 
        } = args;

        return `/higher_level_analysis_color_clarity_distribution?` +
          `seller_account_id=${args.id}` +
          // `&start_date=${args.startDate}` +
          // `&end_date=${args.endDate}` +
          `&shape=${args.shape}` +
          `&carat_range=${args.caratRange}`;
      },

    }),
    getHigherLevelAnalysisPriceMovement: builder.query({
      query: (args) => {
        const { 
          // startDate, 
          // endDate, 
          shape = '0000round', 
          caratRange = '1.00-1.09' 
        } = args;
        
        return `/market_intelligence_grid_data?` +
          `seller_account_id=${args.id}` +
          // `&start_date=${args.startDate}` +
          // `&end_date=${args.endDate}` +
          `&shape=${args.shape}` +
          `&carat_range=${args.caratRange}`;
      },

    }),

    getHigherLevelMarketIntelligenceGridata: builder.query({
      query: (args) => {
        const { 
          // startDate, 
          // endDate, 
          location = '1000global', 
          duration_in_weeks = 4 
        } = args;
        
        return `/market_intelligence_grid_data?` +
          `seller_account_id=${args.id}` +
          `&enumlocation=${args.location}` +
          `&duration_in_weeks=${args.duration_in_weeks}`;
      },

    }),
    getHigherLevelMarketIntelligenceGridataShapeCarat: builder.query({
      query: (args) => {
        const { 
          // startDate, 
          // endDate, 
          shape = '0000round',
          carat_range = '1.00-1.09',
          location = '1000global', 
          duration_in_weeks = 4 
        } = args;
        
        return `/market_intelligence_grid_data?` +
          `seller_account_id=${args.id}` +
          `&shape=${args.shape}` +
          `&carat_range=${args.carat_range}` +
          `&enumlocation=${args.enumlocation}` +
          `&duration_in_weeks=${args.duration_in_weeks}`;
      },

    }),
    
    getHigherLevelAnalysisMovementHistory: builder.query({
      query: (args) => {
        const { 
          // startDate, 
          // endDate, 
          shape = '0000round',
          carat_range = '1.00-1.09',
          location = '1000global', 
          duration_in_weeks = 4 
        } = args;

        return `/market_intelligence_movement_history?` +
          `seller_account_id=${args.id}` +
          `&shape=${args.shape}` +
          `&carat_range=${args.carat_range}`+
          `&color=${args.color}`+
          `&clarity=${args.clarity}`+
          `&enumlocation=${args.location}` +
          `&duration_in_weeks=${args.duration_in_weeks}`
      },

    }),
    getTestHigherLevelAnalysisMovementHistory: builder.query({
      query: (args) => {
        const { 
          // startDate, 
          // endDate, 
          shape = '0000round',
          carat_range = '1.00-1.09',
          location = '1000global', 
          duration_in_weeks = 4 
        } = args;

        return `/market_intelligence_movement_history?` +
          `seller_account_id=${args.id}` +
          `&shape=${args.shape}` +
          `&carat_range=${args.carat_range}`+
          `&color=${args.color}`+
          `&clarity=${args.clarity}`
      },

    }),


    getShapeSizeDistribution: builder.query({
      query: (args) => {
        const { 
          startDate, 
          endDate,
          filters 
        } = args;

        return {
          url: `/bi_analysis_shape_size_distribution?seller_account_id=${args.id}` +
               `&start_date=${startDate}` +
               `&end_date=${endDate}`,
          method: "POST",
          body: filters, // filters will be sent in the request body
        };
      },
      providesTags: ["shapeSizeTable"],
    }),


    getTableDistribution: builder.query({
      query: (args) => {
        
        return {
          url: `/download_bi_analysis_needs_data_json?seller_account_id=${args.id}` +
               `&start_date=${args.startDate}` +
               `&end_date=${args.endDate}`+
               `&type=turn`,
          method: "POST",
          body: args.filters, // filters will be sent in the request body
        };       
      },
    }),
  



    getSupplierTableWithMultiSorting: builder.query({
      // The URL for the request is '/fakeApi/posts'
      query: (cargs) => {
        let args = { ...cargs };

        let searchParams = new URLSearchParams(window.location.search);
        if (searchParams.size > 0){
          for (const p of searchParams) {
            args[p[0]] = p[1]
            if (p[0] == "abs_sort"){
              delete args.sort
            }
          }
        }

        if (args.from === undefined || args.from === null) args.from = 0;
        if (args.size === undefined || args.size === null) args.size = 15;

        if (args.asc_desc === undefined || args.asc_desc === null)
          args.asc_desc = "asc";

        let x = null;
        let y = "items";

        if (args.sort && args.sort.length > 0) {
          x = transformToString(args.sort);
          y = getType(args.sort);
        }

        return `/get_pricing_report_items?seller_account_id=${args.id}&from=${
          args.search && args.search.length > 0 && args.isSearched
            ? 0
            : args.from
        }&size=${args.size}${
          args.sort_by !== null &&
          args.sort_by !== undefined &&
          args.sort_by !== ""
            ? `&sort_by=${encodeURIComponent(args.sort_by)}`
            : ""
        }${
          args.state !== null &&
          args.state !== undefined &&
          args.state !== ""
            ? `&state=${encodeURIComponent(args.state)}`
            : ""
        }&asc_desc=${args.asc_desc}${
          x ? `&${x}` : ""
        }&sort_type=${y}${
          args.search && args.search.length > 0
            ? `&cert_stock_number=${args.search}`
            : ""
        }${
          args.abs_sort !== null &&
          args.abs_sort !== undefined &&
          args.abs_sort !== ""
            ? `&abs_sort=${encodeURIComponent(args.abs_sort)}`
            : ""
        }${
          args.is_filter_locked !== null &&
          args.is_filter_locked !== undefined &&
          args.is_filter_locked !== ""
            ? `&is_filter_locked=${encodeURIComponent(args.is_filter_locked)}`
            : ""
        }`;
      },
      providesTags: ["priceTable"],
    }),

    // getMatchingFromInventory: builder.query({
    //   query: (args) => {
    //     const { id, itemId } = args;
    //     return `/get_pricing_report_items?seller_account_id=${id}&item_id=${itemId}`;
    //   },
    // }),

    // getMatchingFromMarket: builder.query({
    //   query: (args) => {
    //     const { itemId, accId, prrId } = args;

    //     return `/get_pricing_report_market_matching_item_details?item_id=${itemId}&account_id=${accId} ${
    //       prrId ? `&pricing_report_result_id=${prrId}` : ""
    //     }`;
    //   },
    // }),

    getMatchingFromMarket: builder.mutation({
      // useGetMatchingFromRapnetQuery(itemId, accId, prrId, isReset, isFilter, isDefault, from, size, sortBy, lock, multiSort, body)
      query(data) {
        const { args, body } = data;
        const { itemId, itemIds, accId, prrId, isReset, isFilter,isSold , isDefault,isBroad, from, size, sortBy, lock, multiSort } = args;

        let x = null;

        if (multiSort && multiSort.length > 0) {
          x = transformToString(multiSort);
        }

        const resolvedFrom = from ?? 0;
        const resolvedSize = size ?? 50;
        const pricingReportResultIdParam =
          prrId !== null && prrId !== undefined && prrId !== ""
            ? `&pricing_report_result_id=${prrId}`
            : "";
        const itemIdsParam =
          Array.isArray(itemIds) && itemIds.length > 0
            ? `&item_ids=${encodeURIComponent(itemIds.join(","))}`
            : "";
        // if (args.sortBy === undefined || args.sortBy === null) args.sortBy = "rap_discount";
        return {
          url: `/pricing_market_items?item_id=${itemId}&account_id=${accId}${pricingReportResultIdParam}&is_reset=${isReset}&is_sold=${isSold}&is_filter=${isFilter}&is_default=${isDefault}&is_broad=${isBroad}&lock=${lock}&from=${resolvedFrom}&size=${resolvedSize}${itemIdsParam}${x ? `&${x}` : ""}`,
          method: "POST",
          body: body,
        };
      },
    }),

    getMatchingFromRapnet: builder.query({
      query: (args) => {
        const { itemId, accId, prrId } = args;
        return `/get_pricing_report_inventory_matching_item_details?item_id=${itemId}&account_id=${accId} ${
          prrId ? `&pricing_report_result_id=${prrId}` : ""
        }`;
      },
      providesTags: (result, error, arg) => {
        let id = result?.data?.item?.item_id;
        return [`Rapnet matching ${id}`];
      },
    }),

    getConfig: builder.query({
      query: (args) => {
        const { accId } = args;

        return `/get_pricing_report_market_config?seller_account_id=${accId}`;
      },
    }),

    getAsset: builder.query({
      query: (args) => {
        const { itemId } = args;

        return `/get_primary_assets_of_item?item_id=${itemId}`;
      },
    }),

    getSupplierTableWithState: builder.query({
      query: (args) => {
        if (args.from === undefined || args.from === null) args.from = 0;
        if (args.size === undefined || args.size === null) args.size = 15;
        if (args.sort_by === undefined || args.sort_by === null)
          args.sort_by = "sell_offer_cents";
        if (args.asc_desc === undefined || args.asc_desc === null)
          args.asc_desc = "asc";

        let x = null;
        let y = "items";

        if (args.sort) {
          x = transformToString(args.sort);
          y = getType(args.sort);
        }

        return `/get_pricing_report_items?seller_account_id=${args.id}&state=${
          args.state
        }&from=${args.from}&size=${args.size}&sort_by=${encodeURIComponent(
          args.sort_by
        )}&${x}&asc_desc=${args.asc_desc} ${
          args.search && args.search.length > 0
            ? `&cert_stock_number=${args.search}`
            : " "
        }${
          args.is_filter_locked !== null &&
          args.is_filter_locked !== undefined &&
          args.is_filter_locked !== ""
            ? `&is_filter_locked=${encodeURIComponent(args.is_filter_locked)}`
            : ""
        }`;
        // return `/get_pricing_report_items?seller_account_id=${args.id}&from=${args.from}&size=${args.size}&sort_by=${args.sort_by}&asc_desc=${args.asc_desc}`;
      },
      providesTags: ["priceTable"],
    }),

    getFilteredData: builder.mutation({
      query(data) {
        const { args, body } = data;

        if (args.from === undefined || args.from === null) args.from = 0;
        if (args.size === undefined || args.size === null) args.size = 15;
        if (args.sort_by === undefined || args.sort_by === null)
          args.sort_by = "sell_offer_cents";
        if (args.asc_desc === undefined || args.asc_desc === null)
          args.asc_desc = "asc";

        let x = null;
        let y = "items";

        if (args.sort) {
          x = transformToString(args.sort);
          y = getType(args.sort);
        }

        return {
          url: `/get_pricing_report_items?seller_account_id=${args.id}&from=${
            args.search && args.search.length > 0 && args.isSearched
              ? 0
              : args.from
          }&size=${args.size}&sort_by=${encodeURIComponent(
            args.sort_by
          )}&state=${args.state}&asc_desc=${args.asc_desc}&${x}&sort_type=${y}${
            args.search && args.search.length > 0
              ? `&cert_stock_number=${args.search}&sort_type=${y}`
              : ""
          }${
            args.is_filter_locked !== null &&
            args.is_filter_locked !== undefined &&
            args.is_filter_locked !== ""
              ? `&is_filter_locked=${encodeURIComponent(args.is_filter_locked)}`
              : ""
          }`,
          method: "POST",
          body: body,
        };
      },
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetSupplierTableQuery,
  // useGetMatchingFromInventoryQuery,
  useGetMatchingFromRapnetQuery,
  useGetMarketMatchingItemsQuery,
  // useGetSupplierTableWithPaginationQuery,
  useGetSupplierTableWithSortingQuery,
  useGetSupplierTableWithMultiSortingQuery,
  useGetMatchingFromMarketMutation,
  useGetFilteredDataMutation,
  useGetConfigQuery,
  useGetSupplierTableWithStateQuery,
  useGetAssetQuery,
  useGetColorClarityDistributionQuery,
  useGetHigherLevelAnalysisColorClarityQuery,
  useGetHigherLevelAnalysisPriceMovementQuery,
  useGetHigherLevelAnalysisMovementHistoryQuery,
  useGetTestHigherLevelAnalysisMovementHistoryQuery,
  useGetHigherLevelMarketIntelligenceGridataQuery,
  useGetHigherLevelMarketIntelligenceGridataShapeCaratQuery,
  useGetIndividualMovementHistoryQuery,
  useGetTestIndividualMovementHistoryQuery,
  useGetShapeSizeDistributionQuery,
  useGetTableDistributionQuery
} = apiSlice;
